import { useState } from 'react';
import { Building2, CheckCircle2 } from 'lucide-react';
import { ClientFormModal } from '../clients/ClientFormModal';
import { useCreateClient } from '../../hooks/useClients';
import { useInvalidateOnboardingStatus } from '../../hooks/useOnboarding';
import type { OnboardingStatus } from '../../lib/onboardingApi';
import type { ClientInput } from '../../lib/clientsApi';

// Step 1 — Company: register the first client via the ANAF CUI autocomplete
// (reuses ClientFormModal from CLE-122). The invoicing-provider connect block
// that used to sit here was hidden from the UI on 2026-09-29.

interface StepCompanyProps {
  status: OnboardingStatus;
}

export function StepCompany({ status }: StepCompanyProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [createdMsg, setCreatedMsg] = useState<string | null>(null);
  const createClient = useCreateClient();
  const invalidateStatus = useInvalidateOnboardingStatus();

  async function handleCreate(input: ClientInput) {
    const client = await createClient.mutateAsync(input);
    setCreatedMsg(`Client adăugat: ${client.name}.`);
    setModalOpen(false);
    void invalidateStatus();
  }

  return (
    <div className="space-y-6">
      {/* Clients block */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-brand-50 p-2 text-brand-600">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Date companie și clienți</h3>
            <p className="mt-1 text-sm text-gray-500">
              Adăugați primul client folosind completarea automată ANAF după CUI. Introduceți CUI-ul
              și datele se completează automat.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Adaugă client (ANAF)
              </button>
              {status.counts.clients > 0 && (
                <span className="inline-flex items-center gap-1 text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  {status.counts.clients}{' '}
                  {status.counts.clients === 1 ? 'client înregistrat' : 'clienți înregistrați'}
                </span>
              )}
            </div>
            {createdMsg && <p className="mt-2 text-sm text-green-600">{createdMsg}</p>}
            {createClient.isError && (
              <p className="mt-2 text-sm text-red-600">
                {createClient.error instanceof Error
                  ? createClient.error.message
                  : 'Clientul nu a putut fi adăugat.'}
              </p>
            )}
          </div>
        </div>
      </section>

      {modalOpen && (
        <ClientFormModal
          title="Adaugă client"
          submitting={createClient.isPending}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}
